import { useTranslation } from 'react-i18next'
import cn from 'classnames'

import styles from './PlateTouch.module.scss'
import { PlateRegister } from './PlateRegister'

/**
 * CARD 03 — "Suggest first-touch messages". THE DRAFTS THEMSELVES.
 *
 * Three real first-touch messages cycle, each opening on the signal that earned it: the coloured tag
 * names the evidence and the first line quotes the event rather than pitching the product. Text in
 * the figure IS the argument here — the claim is that the message is written from what the person
 * published, and the only way to show that is to show the message.
 *
 * It replaced a diagram of the drafting pipeline. The diagram was accurate and said nothing a buyer
 * could weigh: a channel narrowing through two review lenses does not tell you what lands in an
 * inbox.
 */
const DRAFTS = [
  { key: 'funding', tone: 'move' },
  { key: 'role', tone: 'say' },
  { key: 'hiring', tone: 'reach' },
] as const

/** The bubble outline, authored once and reused by all three: they are the same message frame. */
const BUBBLE =
  'M14 26h332a10 10 0 0 1 10 10v62a10 10 0 0 1-10 10H44l-10 12-3-12H14a10 10 0 0 1-10-10V36a10 10 0 0 1 10-10z'

/**
 * `compact` is not a smaller variant — it is a LARGER type variant. The onboarding card renders this
 * same 360-unit viewBox at about 241px, which would put the landing's 12.5px line on screen at
 * 8.4px. The type is scaled by that ratio and the copy shortened to match, because the lines ARE the
 * argument here and an unreadable argument is decoration.
 */
export const PlateTouch = ({ compact = false }: { compact?: boolean }) => {
  const { t } = useTranslation()
  const copy = compact ? 'short' : 'long'

  return (
    <div className={cn(styles.plate, { [styles.compact as string]: compact })}>
      <svg viewBox="0 0 360 160" aria-hidden focusable="false">
        <PlateRegister
          figure="03"
          hairClassName={styles.hair as string}
          hair2ClassName={styles.hair2 as string}
          numberClassName={styles.fno as string}
        />

        {DRAFTS.map((draft, index) => (
          <g
            key={draft.key}
            className={styles.message}
            // 12s loop, one draft every 4s: long enough to read two lines without hurrying.
            style={{ animationDelay: `${index * 4}s` }}
          >
            <path className={styles.bubble} d={BUBBLE} />
            <circle className={cn(styles.tagDot, styles[`tone-${draft.tone}`])} cx={26} cy={45} r={4} />
            <text className={cn(styles.tag, styles[`tone-${draft.tone}`])} x={36} y={49}>
              {t(`landing.features.drafts.${draft.key}.tag`)}
            </text>
            <text className={styles.line} x={26} y={compact ? 72 : 74}>
              {t(`landing.features.drafts.${draft.key}.${copy}.first`)}
            </text>
            <text className={styles.line} x={26} y={compact ? 96 : 93}>
              {t(`landing.features.drafts.${draft.key}.${copy}.second`)}
            </text>
          </g>
        ))}
      </svg>
    </div>
  )
}
